import { differenceInMinutes } from 'date-fns'
import type { FC } from 'react'
import { useOutletContext } from 'react-router'
import { ButtonBrandLegacy } from '@/components/ui/button-brand-legacy'
import { ArrowRightIcon, CheckCircleIcon } from '@/components/ui/icons'
import { Spinner } from '@/components/ui/spinner'
import { useGetPhotoset } from '@/features/photoset/queries/use-get-photoset'
import { useUpdateStepToCuration } from '@/features/photoset/queries/use-update-step-to-curation'
import { usePhotosetStore } from '@/features/photoset/store/store'
import type { PhotosetURLParams } from '@/features/photoset/types.ts'
import { errorHandler } from '@/helpers/utils'
import { QUERY_KEYS, queryClient } from '@/query-client'
import { useTranslation } from '@/translations/i18n'

export const CompletedAllocationBox: FC<{ imagesCount: number }> = ({
	imagesCount
}) => {
	const { t } = useTranslation()
	const { urlParams } = useOutletContext<{ urlParams: PhotosetURLParams }>()
	const photoset = useGetPhotoset(urlParams, { enabled: false })
	const unselectAll = usePhotosetStore((state) => state.unselectAll)
	const updateStepToCurationMutation = useUpdateStepToCuration({
		onError: (error) => errorHandler(error),
		onSuccess: () => {
			unselectAll()
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.PHOTOSET]
			})
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.IMAGES]
			})
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.PHOTOSET_SPACES]
			})
		}
	})

	const completedAt = photoset.data?.initial_qa_completed_at
	const minutesCount = completedAt
		? differenceInMinutes(new Date(), completedAt)
		: 0

	return (
		<div className='flex flex-col justify-center gap-8 p-6 [&>svg]:fill-success-600'>
			<CheckCircleIcon size={40} />
			<div className='flex flex-col gap-4'>
				<div className='flex flex-col gap-2'>
					<h3 className='font-bold text-2xl text-neutral-900'>
						{t('photoset:allocation.completed_title')}
					</h3>
					<h4 className='text-neutral-900 text-xl'>
						{t('photoset:allocation.completed_subtitle')}
					</h4>
				</div>
				{minutesCount <= 5 && (
					<p className='text-base text-success-600'>
						{t('photoset:allocation.completed_success_subtitle', {
							imagesCount,
							minutesCount
						})}
					</p>
				)}
			</div>
			<ButtonBrandLegacy
				disabled={updateStepToCurationMutation.isPending}
				onClick={() =>
					updateStepToCurationMutation.mutate(urlParams.id)
				}
			>
				{updateStepToCurationMutation.isPending && (
					<Spinner size={20} />
				)}
				{t('common:curation')}
				<ArrowRightIcon />
			</ButtonBrandLegacy>
		</div>
	)
}

CompletedAllocationBox.displayName = 'CompletedAllocationBox'
